<template>
  <Container
    ref="parallaxArea"
    tag="header"
    class="relative grid h-full min-h-[60svh] flex-col overflow-hidden bg-gray-500 pb-28 text-beige-500 max-sm:gap-16 sm:min-h-[95svh] lg:grid-cols-2"
  >
    <!-- HEADER -->
    <div
      class="absolute inset-x-0 top-0 z-50 flex justify-between px-container py-4 sm:px-container-md"
    >
      <!-- home -->
      <AppLink
        class="relative h-full w-[16.5rem]"
        :to="localePath('/')"
        :aria-label="$t('homepage')"
      >
        <IconBaryonBanner
          is-small
          animate
          class="w-full text-beige-500 transition-colors"
        />
      </AppLink>

      <!-- Close -->
      <NuxtLink
        class="mt-4 size-10 text-beige-500 transition-colors hover:text-beige-600 max-lg:hidden"
        :to="localePath(`/${router.currentRoute.value?.params.uri[0]}`)"
        :aria-label="$t('team_overview')"
      >
        <IconCross class="size-full" />
      </NuxtLink>
    </div>

    <!-- LEFT -->
    <AnimatedContainer
      class="relative z-10 h-full flex-col justify-center px-container max-lg:order-2 sm:flex sm:px-container-lg sm:pt-32"
    >
      <ParallaxElement :tilt="tilt" :roll="roll">
        <!-- Category -->
        <div
          class="slide-up z-10 mb-7 border-b border-gray-300 pb-2 typo-heading-xs lg:-mr-80"
        >
          <span>{{ data.category.title }}</span>
        </div>

        <!-- Title -->
        <TextAppear
          v-if="data?.title"
          tag="h1"
          class="slide-up mb-2 typo-heading-xl md:typo-heading-2xl xl:typo-heading-3xl"
          :text="data.title"
          :style="{
            '--character-appear-from': '#222B41',
            '--character-appear-transition': '#D9D5CE',
            '--character-appear-to': '#D9D5CE',
          }"
          :duration="666"
        />
        <!-- Competencies -->
        <p
          v-if="data.competencies"
          class="slide-up mb-10 whitespace-pre-wrap text-gray-200 typo-body-sm sm:mb-16"
          v-html="data.competencies"
        />

        <!-- Text -->
        <div
          v-if="data.text"
          class="slide-up mb-16 typo-body-sm sm:typo-body-md lg:-mr-10"
          v-html="data.text"
        />

        <div class="slide-up flex flex-wrap gap-8 text-gray-200">
          <!-- Phone -->
          <a
            v-if="data.phone"
            :href="`tel:${data.phone}`"
            class="block transition-colors typo-label-sm hover:text-gray-300 sm:typo-label-md"
          >
            {{ $t('phone_short') }} {{ data.phone }}
          </a>

          <!-- Email -->
          <a
            v-if="data.email"
            :href="`mailto:${data.email}`"
            class="block transition-colors typo-label-sm hover:text-gray-300 sm:typo-label-md"
          >
            {{ data.email }}
          </a>
        </div>
      </ParallaxElement>
    </AnimatedContainer>

    <!-- RIGHT -->
    <AnimatedContainer
      class="relative pl-28 max-lg:order-1 sm:pl-[40%] lg:pl-26 lg:pr-[10.5rem]"
    >
      <ParallaxElement :tilt="-tilt" :roll="-roll">
        <div class="slide-right aspect-[435/630] lg:w-full">
          <AppImage
            v-if="data?.image"
            :src="data.image"
            class="size-full -translate-y-5 object-cover max-lg:translate-x-5"
            loading="eager"
          />
        </div>
      </ParallaxElement>
    </AnimatedContainer>

    <!-- SCROLLY -->
    <Scrolly class="absolute bottom-20 right-container-md w-10 max-lg:hidden" />
  </Container>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { useNavigationStore, NavigationStyle } from '~/stores/navigation'

defineProps({
  data: { type: Object, required: true },
})

const { navigationStyle } = storeToRefs(useNavigationStore())

// Parallax
const parallaxArea = ref(null)
const { tilt, roll } = useParallax(parallaxArea)

const localePath = useLocalePath()
const router = useRouter()

onMounted(async () => {
  await nextTick()
  navigationStyle.value = NavigationStyle.Hidden
})

onUnmounted(() => {
  navigationStyle.value = NavigationStyle.Dark
})
</script>
